import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cpcApi } from './cpc-api';
import type {
  CalculateAssessmentPayload,
  SetActualPtPayload,
  CreateFactorSetPayload,
  UpdateFactorSetPayload,
  CreateRoleRatePayload,
  UpdateRoleRatePayload,
  LinkExistingProjectPayload,
  CreateClockodoProjectPayload,
  UpdateClockodoConfigPayload,
  CreateServiceMappingPayload,
  AuditLogFilter,
} from './cpc-types';

export const cpcKeys = {
  factorSets: {
    all: ['cpc', 'factor-sets'] as const,
    active: ['cpc', 'factor-sets', 'active'] as const,
    detail: (id: string) => ['cpc', 'factor-set-detail', id] as const,
  },
  calculations: {
    byCompany: (companyId: string) => ['cpc', 'calculations', companyId] as const,
    detail: (id: string) => ['cpc', 'calculation', id] as const,
    logEntries: (id: string) => ['cpc', 'clockodo-entries', id] as const,
  },
  roleRates: ['cpc', 'role-rates'] as const,
  roleRatesAll: ['cpc', 'role-rates-all'] as const,
  clockodo: {
    config: ['cpc', 'clockodo-config'] as const,
    services: ['cpc', 'clockodo-services'] as const,
    mappings: ['cpc', 'clockodo-mappings'] as const,
    health: ['cpc', 'clockodo-health'] as const,
  },
  reporting: {
    planVsIst: ['cpc', 'reporting', 'plan-vs-ist'] as const,
  },
  auditLog: (filter: AuditLogFilter) => ['cpc', 'audit-log', filter] as const,
};

export function useCpcFactorSets() {
  return useQuery({
    queryKey: cpcKeys.factorSets.all,
    queryFn: cpcApi.listFactorSets,
  });
}

export function useCpcFactorSetDetail(id: string | null) {
  return useQuery({
    queryKey: cpcKeys.factorSets.detail(id ?? ''),
    queryFn: () => cpcApi.getFactorSet(id!),
    enabled: !!id,
  });
}

export function useCreateFactorSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFactorSetPayload) => cpcApi.createFactorSet(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.factorSets.all });
    },
  });
}

export function useUpdateFactorSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFactorSetPayload }) =>
      cpcApi.updateFactorSet(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.factorSets.all });
      queryClient.invalidateQueries({ queryKey: cpcKeys.factorSets.detail(id) });
    },
  });
}

export function useDeleteFactorSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cpcApi.deleteFactorSet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.factorSets.all });
    },
  });
}

export function useActivateFactorSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cpcApi.activateFactorSet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.factorSets.all });
      queryClient.invalidateQueries({ queryKey: cpcKeys.factorSets.active });
    },
  });
}

export function useCpcCalculations(companyId: string) {
  return useQuery({
    queryKey: cpcKeys.calculations.byCompany(companyId),
    queryFn: () => cpcApi.listCalculations(companyId),
    enabled: !!companyId,
  });
}

export function useCpcCalculation(id: string | null) {
  return useQuery({
    queryKey: cpcKeys.calculations.detail(id ?? ''),
    queryFn: () => cpcApi.getCalculation(id!),
    enabled: !!id,
  });
}

export function useCpcActiveFactorSet() {
  return useQuery({
    queryKey: cpcKeys.factorSets.active,
    queryFn: cpcApi.getActiveFactorSet,
  });
}

export function useCpcRoleRates() {
  return useQuery({
    queryKey: cpcKeys.roleRates,
    queryFn: cpcApi.getRoleRates,
  });
}

export function useCalculateAssessment(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CalculateAssessmentPayload) => cpcApi.calculate(companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.byCompany(companyId) });
    },
  });
}

export function useDeleteCalculation(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cpcApi.deleteCalculation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.byCompany(companyId) });
    },
  });
}

export function useSetActualPt(calculationId: string, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetActualPtPayload) => cpcApi.setActualPt(calculationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.byCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.detail(calculationId) });
    },
  });
}

export function useCpcAllRoleRates() {
  return useQuery({
    queryKey: cpcKeys.roleRatesAll,
    queryFn: cpcApi.getAllRoleRates,
  });
}

export function useCreateRoleRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoleRatePayload) => cpcApi.createRoleRate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.roleRates });
      queryClient.invalidateQueries({ queryKey: cpcKeys.roleRatesAll });
    },
  });
}

export function useUpdateRoleRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRatePayload }) =>
      cpcApi.updateRoleRate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.roleRates });
      queryClient.invalidateQueries({ queryKey: cpcKeys.roleRatesAll });
    },
  });
}

export function useDeleteRoleRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cpcApi.deleteRoleRate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.roleRates });
      queryClient.invalidateQueries({ queryKey: cpcKeys.roleRatesAll });
    },
  });
}

// ── Clockodo-Hooks ─────────────────────────────────────────────

export function useActualLogEntries(calcId: string | null) {
  return useQuery({
    queryKey: cpcKeys.calculations.logEntries(calcId ?? ''),
    queryFn: () => cpcApi.getActualLogEntries(calcId!),
    enabled: !!calcId,
  });
}

export function useLinkExistingProject(calcId: string, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LinkExistingProjectPayload) => cpcApi.linkExistingProject(calcId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.byCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.detail(calcId) });
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.logEntries(calcId) });
    },
  });
}

export function useCreateClockodoProject(calcId: string, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateClockodoProjectPayload) => cpcApi.createClockodoProject(calcId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.byCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.detail(calcId) });
    },
  });
}

export function useManualSync(calcId: string, companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cpcApi.manualSync(calcId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.byCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: cpcKeys.calculations.logEntries(calcId) });
    },
  });
}

export function useClockodoConfig() {
  return useQuery({
    queryKey: cpcKeys.clockodo.config,
    queryFn: cpcApi.getClockodoConfig,
    retry: false,
  });
}

export function useUpdateClockodoConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateClockodoConfigPayload) => cpcApi.updateClockodoConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.clockodo.config });
    },
  });
}

export function useClockodoServices(enabled = true) {
  return useQuery({
    queryKey: cpcKeys.clockodo.services,
    queryFn: cpcApi.getClockodoServices,
    enabled,
    retry: false,
  });
}

export function useServiceMappings() {
  return useQuery({
    queryKey: cpcKeys.clockodo.mappings,
    queryFn: cpcApi.getServiceMappings,
  });
}

export function useCreateServiceMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateServiceMappingPayload) => cpcApi.createServiceMapping(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.clockodo.mappings });
    },
  });
}

export function useDeleteServiceMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cpcApi.deleteServiceMapping(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cpcKeys.clockodo.mappings });
    },
  });
}

export function useClockodoSyncHealth() {
  return useQuery({
    queryKey: cpcKeys.clockodo.health,
    queryFn: cpcApi.getSyncHealth,
    refetchInterval: 60_000,
  });
}

// ── Reporting-Hooks ────────────────────────────────────────────

export function usePlanVsIst() {
  return useQuery({
    queryKey: cpcKeys.reporting.planVsIst,
    queryFn: cpcApi.getPlanVsIst,
    staleTime: 30_000,
  });
}

// ── Audit-Log-Hooks ────────────────────────────────────────────

export function useAuditLog(filter: AuditLogFilter) {
  return useQuery({
    queryKey: cpcKeys.auditLog(filter),
    queryFn: () => cpcApi.getAuditLog(filter),
    staleTime: 10_000,
  });
}
